/**
 * dom-id-rewriter.js — applies a descendant-id rename map to a section's DOM
 * at export time (Workstream A2).
 *
 * Renames every `[id]` the map covers and rewrites, WITHIN the section
 * subtree, every reference the id-reference-registry declares: single-IDREF
 * attributes, space-separated IDREF lists, same-document fragment hrefs,
 * usemap/map[name] pairs, and `url(#id)` in SVG presentation attributes and
 * inline styles.
 *
 * Scope rule: fragment hrefs (`href="#x"`, `xlink:href="#x"`) are rewritten
 * ONLY for ids in THIS instance's rename map — a section linking to another
 * section's anchor is the final link layer's job (A5), not this pass's.
 *
 * Never touched: `data-pb-eid`, `data-style-id`, or any other data-* value —
 * those are editable identities, not element ids.
 */

import {
  SINGLE_IDREF_ATTRS,
  IDREF_LIST_ATTRS,
  FRAGMENT_URL_ATTRS,
  URL_FUNCTION_ATTRS,
  USEMAP_ATTR,
  splitIdrefList,
  splitFragmentUrl
} from '../shared/id-reference-registry.js';

function rewriteUrlFunctions(value, renameMap) {
  return String(value).replace(/url\(\s*(['"]?)#([^'")\s]+)\1\s*\)/g, (whole, quote, id) =>
    renameMap.has(id) ? `url(${quote}#${renameMap.get(id)}${quote})` : whole
  );
}

/**
 * Apply `renameMap` (oldId → newId) to the section subtree rooted at `root`.
 * Mutates the DOM in place; returns the count of attribute rewrites made
 * (excluding the id attributes themselves), which tests use to prove
 * coverage is non-vacuous.
 */
export function applyIdRenamesToSectionDOM(root, renameMap) {
  if (!root || !(renameMap instanceof Map) || renameMap.size === 0) return 0;
  let rewrites = 0;
  const all = [root, ...root.querySelectorAll('*')];

  for (const el of all) {
    if (typeof el.getAttribute !== 'function') continue;

    const id = el.getAttribute('id');
    if (id && renameMap.has(id)) el.setAttribute('id', renameMap.get(id));

    for (const attr of SINGLE_IDREF_ATTRS) {
      const v = el.getAttribute(attr);
      if (v && renameMap.has(v.trim())) {
        el.setAttribute(attr, renameMap.get(v.trim()));
        rewrites++;
      }
    }

    for (const attr of IDREF_LIST_ATTRS) {
      const v = el.getAttribute(attr);
      if (!v) continue;
      const tokens = splitIdrefList(v);
      if (!tokens.some((t) => renameMap.has(t))) continue;
      el.setAttribute(attr, tokens.map((t) => (renameMap.has(t) ? renameMap.get(t) : t)).join(' '));
      rewrites++;
    }

    for (const attr of FRAGMENT_URL_ATTRS) {
      const v = el.getAttribute(attr);
      const parsed = splitFragmentUrl(v);
      // Same-document fragments only (empty path) — see the scope rule above.
      if (!parsed || parsed.path !== '' || !renameMap.has(parsed.id)) continue;
      el.setAttribute(attr, `#${renameMap.get(parsed.id)}`);
      rewrites++;
    }

    const usemap = el.getAttribute(USEMAP_ATTR);
    if (usemap && usemap.startsWith('#') && renameMap.has(usemap.slice(1))) {
      el.setAttribute(USEMAP_ATTR, `#${renameMap.get(usemap.slice(1))}`);
      rewrites++;
    }
    if (el.tagName === 'MAP') {
      const name = el.getAttribute('name');
      if (name && renameMap.has(name)) {
        el.setAttribute('name', renameMap.get(name));
        rewrites++;
      }
    }

    for (const attr of URL_FUNCTION_ATTRS) {
      const v = el.getAttribute(attr);
      if (!v || !v.includes('url(')) continue;
      const next = rewriteUrlFunctions(v, renameMap);
      if (next !== v) {
        el.setAttribute(attr, next);
        rewrites++;
      }
    }
  }

  return rewrites;
}
